import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Wrench } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { erroreDi, giorno, oggi, piuGiorni } from "@/lib/crm";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import CampoData from "@/components/common/CampoData";
import SceltaCliente from "@/components/common/SceltaCliente";
import { StatusBadge } from "@/components/ui/status-badge";
import { cn } from "@/lib/utils";

/**
 * L'assistenza (0039): un promemoria per persone, non un sistema di ticket.
 * Tre stati e basta. La usano la pagina Assistenza e la scheda del cliente.
 */
export const STATI_ASSISTENZA = {
  da_fare: { label: "Da fare", tono: "warning" },
  programmata: { label: "Programmata", tono: "brand" },
  fatta: { label: "Fatta", tono: "success" },
};

const NESSUNO = "nessuno";

/** «Da fissare» · «oggi» · «domani» · «gio 30 ott». */
export function quando(data) {
  if (!data) return "Da fissare";
  if (data === oggi()) return "oggi";
  if (data === piuGiorni(oggi(), 1)) return "domani";
  if (data === piuGiorni(oggi(), -1)) return "ieri";
  return giorno(data);
}

/** Chi può fare un'assistenza: con il cliente, solo chi la vedrà (il server sa chi).
 *  `attivo` falso per il montatore, che non assegna: il server gli risponderebbe 403. */
export function usePersone(clienteId, attivo = true) {
  const [persone, setPersone] = useState([]);
  useEffect(() => {
    if (!attivo) return undefined;
    let vivo = true;
    api
      .get("/assistenze/persone", { params: { cliente_id: clienteId || undefined } })
      .then((r) => vivo && setPersone(r.data || []))
      .catch(() => vivo && setPersone([]));
    return () => {
      vivo = false;
    };
  }, [clienteId, attivo]);
  return persone;
}

/** Il montatore aggiorna le sue assistenze, non ne crea e non le riassegna. */
export function useMontatore() {
  const { profile } = useAuth() || {};
  return profile?.ruolo === "montatore";
}

export function StatoAssistenza({ stato }) {
  const s = STATI_ASSISTENZA[stato] || { label: stato, tono: "neutral" };
  return (
    <StatusBadge tone={s.tono} data-testid="assistenza-stato">
      {s.label}
    </StatusBadge>
  );
}

/** Quando, per una riga: «oggi», «gio 30 ott», «Da fissare»; la scaduta lo dice con un pallino. */
function Quando({ a }) {
  return (
    <span className={cn("inline-flex items-center gap-1.5", a.scaduta ? "font-semibold text-warning-ink" : "")}>
      {a.scaduta ? <span className="size-1.5 shrink-0 rounded-full bg-warning" aria-hidden /> : null}
      {quando(a.data_prevista)}
      {a.scaduta ? " · scaduta" : ""}
    </span>
  );
}

/**
 * Le assistenze di un cliente, dentro la sua scheda (la colonna è stretta): una voce per
 * assistenza su due righe. La pagina Assistenza ha il suo elenco, affiancato al dettaglio.
 */
export function ElencoAssistenze({ righe, onApri }) {
  return (
    <ul className="-mx-inside grid divide-y divide-line-subtle">
      {righe.map((a) => (
        <li key={a.id}>
          <button
            type="button"
            onClick={() => onApri(a)}
            data-testid={`assistenza-${a.id}`}
            className="grid min-h-11 w-full gap-micro rounded-md px-inside py-group text-left text-body transition-colors duration-150 ease-enter hover:bg-surface-2 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand"
          >
            <span className="flex items-start justify-between gap-group font-semibold text-ink-strong">
              <span className="min-w-0">{a.descrizione}</span>
              <StatoAssistenza stato={a.stato} />
            </span>
            <span className="text-ink-2">{a.luogo || <span>Luogo da indicare</span>}</span>
            <span className="text-ink-2">
              <Quando a={a} /> · {a.assegnato || "da assegnare"}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}

/**
 * Nuova assistenza o modifica. Una nuova nasce sempre con un cliente: se non arriva
 * da fuori (`cliente`), si sceglie per primo. Il luogo vuoto lo riempie il database
 * con l'indirizzo della scheda cliente.
 */
export function AssistenzaDialog({ open, onOpenChange, assistenza, cliente, onFatto }) {
  const nuova = !assistenza;
  // Il responsabile di sede vede le assistenze dei clienti dei colleghi, non le cambia (0043).
  const sola = assistenza?.modificabile === false;
  const montatore = useMontatore();
  const [scelto, setScelto] = useState(null);
  const persone = usePersone(assistenza?.cliente_id || cliente?.id || scelto?.id, !montatore);
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setScelto(cliente || null);
    setF({
      descrizione: assistenza?.descrizione || "",
      luogo: assistenza?.luogo || "",
      data_prevista: assistenza?.data_prevista || "",
      assegnato_a: assistenza?.assegnato_a || NESSUNO,
      stato: assistenza?.stato || "da_fare",
    });
  }, [open, assistenza, cliente]);
  const campo = (k) => (e) => setF((x) => ({ ...x, [k]: e?.target ? e.target.value : e }));

  const salva = async (e) => {
    e.preventDefault();
    setBusy(true);
    const corpo = {
      descrizione: f.descrizione,
      luogo: f.luogo,
      data_prevista: f.data_prevista || null,
      ...(montatore ? {} : { assegnato_a: f.assegnato_a === NESSUNO ? null : f.assegnato_a }),
    };
    try {
      if (nuova) await api.post("/assistenze", { ...corpo, cliente_id: scelto.id });
      else await api.patch(`/assistenze/${assistenza.id}`, { ...corpo, stato: f.stato });
      onOpenChange(false);
      onFatto(nuova ? "Assistenza aggiunta" : "Assistenza aggiornata");
    } catch (err) {
      toast.error(erroreDi(err));
    } finally {
      setBusy(false);
    }
  };

  const nomeCliente = assistenza?.cliente || scelto?.nome;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="assistenza-modal">
        <form onSubmit={salva} className="grid gap-card">
          <DialogHeader>
            <DialogTitle>{nuova ? "Nuova assistenza" : "Assistenza"}</DialogTitle>
            <DialogDescription>
              {nomeCliente && (cliente || !nuova) ? `Per ${nomeCliente}. ` : ""}
              {sola ? "Il cliente lo segue un collega: la vedi, la cambia chi lo segue." : "Cosa c'è da fare, dove, quando e chi la fa."}
            </DialogDescription>
          </DialogHeader>
          {/* In sola lettura i campi si leggono a pieno contrasto: lo spento dei componenti è al 50%. */}
          <fieldset disabled={sola} className="grid min-w-0 gap-card [&_:disabled]:!opacity-100" data-testid="assistenza-campi">
            {nuova && !cliente ? (
              <div className="grid gap-inside" role="group" aria-labelledby="as-cliente">
                <Label id="as-cliente">Cliente</Label>
                <SceltaCliente
                  valore={scelto?.id}
                  onScegli={(c) => {
                    setScelto(c);
                    setF((x) => ({ ...x, assegnato_a: NESSUNO })); // chi la fa dipende dal cliente
                  }}
                  testId="assistenza-cliente"
                  segnaposto="Cerca il cliente per nome"
                />
              </div>
            ) : null}
            <div className="grid gap-inside">
              <Label htmlFor="as-cosa">Cosa</Label>
              <Input
                id="as-cosa"
                value={f.descrizione || ""}
                onChange={campo("descrizione")}
                placeholder="Anta della cucina da regolare"
                required
                data-testid="assistenza-cosa"
              />
            </div>
            <div className="grid gap-inside">
              <Label htmlFor="as-dove">Dove</Label>
              <Input
                id="as-dove"
                value={f.luogo || ""}
                onChange={campo("luogo")}
                placeholder={nuova ? "L'indirizzo della scheda cliente" : "Indirizzo dell'intervento"}
                aria-describedby={nuova ? "as-dove-nota" : undefined}
                data-testid="assistenza-dove"
              />
              {nuova ? (
                <p id="as-dove-nota" className="text-small text-ink-2">
                  Vuoto: si usa l'indirizzo della scheda cliente. Lo puoi cambiare dopo.
                </p>
              ) : null}
            </div>
            <div className="grid gap-card sm:grid-cols-2">
              <div className="grid content-start gap-inside">
                <Label htmlFor="as-quando">Quando</Label>
                <CampoData id="as-quando" value={f.data_prevista || ""} onChange={campo("data_prevista")} testId="assistenza-quando" />
              </div>
              <div className="grid content-start gap-inside">
                <Label htmlFor="as-chi">Chi la fa</Label>
                <Select value={f.assegnato_a || NESSUNO} onValueChange={campo("assegnato_a")} disabled={sola || montatore || (nuova && !cliente && !scelto)}>
                  <SelectTrigger id="as-chi" className="h-11 sm:h-9" data-testid="assistenza-chi">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NESSUNO}>Da assegnare</SelectItem>
                    {montatore && assistenza?.assegnato_a ? <SelectItem value={assistenza.assegnato_a}>{assistenza.assegnato}</SelectItem> : null}
                    {persone.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {nuova ? null : (
              <div className="grid gap-inside">
                <Label htmlFor="as-stato">Stato</Label>
                <Select value={f.stato} onValueChange={campo("stato")} disabled={sola}>
                  <SelectTrigger id="as-stato" className="h-11 sm:h-9" data-testid="assistenza-stato-scelta">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATI_ASSISTENZA).map(([k, s]) => (
                      <SelectItem key={k} value={k} data-testid={`assistenza-stato-${k}`}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </fieldset>
          <DialogFooter className="gap-inside">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {sola ? "Chiudi" : "Annulla"}
            </Button>
            {sola ? null : (
              <Button type="submit" disabled={busy || !(f.descrizione || "").trim() || (nuova && !scelto)} data-testid="assistenza-salva">
                {busy ? "Salvo…" : "Salva"}
              </Button>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** La sezione nella scheda del cliente: le sue assistenze e il bottone per aggiungerne una. */
export function AssistenzeCliente({ cliente }) {
  const { profile } = useAuth() || {};
  // Un venditore le apre solo sui clienti che segue; quelle del collega (da responsabile
  // della sede) le vede e basta (0043).
  const scrive = profile?.ruolo !== "venditore" || cliente.venditore_id === profile?.id;
  const [righe, setRighe] = useState(null);
  const [aperta, setAperta] = useState(undefined); // undefined = chiuso, null = nuova, riga = modifica
  const perDialog = useMemo(() => ({ id: cliente.id, nome: cliente.nome_visualizzato }), [cliente.id, cliente.nome_visualizzato]);
  const carica = useCallback(() => {
    api
      .get("/assistenze", { params: { cliente_id: cliente.id } })
      .then((r) => setRighe(r.data?.righe || []))
      .catch(() => setRighe([]));
  }, [cliente.id]);
  useEffect(carica, [carica]);
  const fatto = (msg) => {
    toast.success(msg);
    carica();
  };
  return (
    <Card className="p-card" data-testid="cliente-assistenza">
      <div className="flex items-center justify-between gap-group">
        <h2 className="text-base font-semibold text-foreground">Assistenza</h2>
        {scrive ? (
          <Button variant="outline" size="sm" onClick={() => setAperta(null)} data-testid="cliente-nuova-assistenza">
            <Plus />
            Nuova assistenza
          </Button>
        ) : (
          <p className="text-xs text-muted-foreground" data-testid="cliente-assistenza-sola-lettura">
            Le apre chi segue il cliente
          </p>
        )}
      </div>
      <div className="mt-group">
        {righe === null ? null : righe.length ? (
          <ElencoAssistenze righe={righe} onApri={setAperta} />
        ) : (
          <p className="flex items-center gap-inside text-sm text-muted-foreground">
            <Wrench className="h-4 w-4 shrink-0" aria-hidden />
            Nessuna assistenza per questo cliente.
          </p>
        )}
      </div>
      <AssistenzaDialog
        open={aperta !== undefined}
        onOpenChange={(v) => !v && setAperta(undefined)}
        assistenza={aperta || null}
        cliente={perDialog}
        onFatto={fatto}
      />
    </Card>
  );
}
